import { useState, useRef, useEffect } from 'react';

import { readCache, writeCache } from '~helpers/sessionCache';
import { buildCacheKey } from './useListingState';

// Holds a listing's filter state with the same TTL-based sessionStorage persistence
// that useListingState applies to the results, so a filtered list restores together
// with the selections that produced it on return navigation. Key on the same
// cacheKeyParts as the results snapshot - the two halves share identity and expiry.
//
// wasRestored() tells the caller a snapshot seeded the state; default-initialization
// effects should skip their overwrite in that case or the restore is silently undone.
//
// Persistence only runs while `enabled` is true. The caller must disable it while an
// async key part (e.g. locationId) is still resolving - reading or writing under a
// partial key would leak one search's snapshot into another (e.g. across cities).

const FIVE_MINUTES = 5 * 60 * 1000;

// A snapshot written before the price initialization finished carries price: null;
// restoring it would skip that initialization and send criteria the API rejects.
const isUsable = (cached) => !!cached && cached.price !== null && cached.price !== undefined;

export const usePersistedFilterState = ({ cacheKeyParts = [], ttl = FIVE_MINUTES, defaultFilters, enabled = true }) => {
  const cacheKey = enabled ? `${buildCacheKey(cacheKeyParts)}:filters` : null;

  // Read the snapshot once, synchronously, so the controls render restored on first paint.
  const initialRef = useRef(undefined);
  if (initialRef.current === undefined) {
    const cached = cacheKey ? readCache(cacheKey, ttl) : null;
    initialRef.current = isUsable(cached) ? cached : null;
  }

  const restoredRef = useRef(!!initialRef.current);

  const [filters, setFilters] = useState(initialRef.current || defaultFilters);
  const [touched, setTouched] = useState(false);

  // Parts like locationId can resolve after mount, changing the key. Restore the new
  // key's snapshot then - unless the user already interacted this mount. On a miss keep
  // the current filters (in-mount navigation such as paging must not reset them).
  // Declared before the write effect so the read happens before the new key is written.
  const seededKeyRef = useRef(cacheKey);
  useEffect(() => {
    if (seededKeyRef.current === cacheKey) return;
    seededKeyRef.current = cacheKey;

    if (!cacheKey || touched) return;

    const cached = readCache(cacheKey, ttl);
    if (isUsable(cached)) {
      restoredRef.current = true;
      setFilters(cached);
    }
  }, [cacheKey]);

  // Persist the current selections so the next return navigation can restore them.
  useEffect(() => {
    if (!cacheKey) return;
    writeCache(cacheKey, filters);
  }, [cacheKey, filters]);

  const wasRestored = () => restoredRef.current;

  return { filters, setFilters, touched, setTouched, wasRestored };
};
